import React, { useEffect, useRef, useState } from 'react';

const formatQuantityDisplayValue = (nextValue: number) => {
  if (!Number.isFinite(nextValue)) {
    return '';
  }
  const normalized = Number(nextValue.toFixed(6));
  return Number.isInteger(normalized) ? normalized.toFixed(0) : String(normalized);
};

const parseQuantityInputValue = (nextValue: string) =>
  Number.parseFloat(nextValue.trim().replace(',', '.'));

const stopInteractionPropagation = (event: React.SyntheticEvent) => {
  event.stopPropagation();
};

const stopNativeInteractionPropagation = (event: Event) => {
  event.stopPropagation();
};

const selectInputContents = (inputElement: HTMLInputElement) => {
  inputElement.select();
};

export function QuantitySelector({
  value,
  min,
  max,
  stack = 0.1,
  isMax = false,
  ariaLabel = 'Quantity value',
  decrementLabel = 'Decrease value',
  incrementLabel = 'Increase value',
  onChange,
  onCommit,
}: {
  value: number;
  min: number;
  max: number;
  stack?: number;
  isMax?: boolean;
  ariaLabel?: string;
  decrementLabel?: string;
  incrementLabel?: string;
  onChange: (newValue: number) => void;
  onCommit?: (newValue: number) => void;
}) {
  const quantityReference = useRef<HTMLDivElement>(null);
  const isEditingReference = useRef(false);
  const [inputValue, setInputValue] = useState<string>(() => formatQuantityDisplayValue(value));
  const parsedInputValue = parseQuantityInputValue(inputValue);

  useEffect(() => {
    const quantityElement = quantityReference.current;
    if (!quantityElement) {
      return;
    }

    const interactionElements = quantityElement.querySelectorAll('button, input');
    const options = false;
    const eventTypes = ['pointerdown', 'mousedown', 'touchstart'] as const;
    for (const interactionElement of interactionElements) {
      for (const eventType of eventTypes) {
        interactionElement.addEventListener(eventType, stopNativeInteractionPropagation, options);
      }
    }

    return () => {
      for (const interactionElement of interactionElements) {
        for (const eventType of eventTypes) {
          interactionElement.removeEventListener(eventType, stopNativeInteractionPropagation, options);
        }
      }
    };
  }, []);

  useEffect(() => {
    if (!isEditingReference.current) {
      setInputValue(formatQuantityDisplayValue(value));
    }
  }, [value]);

  const finalizeInput = () => {
    let desiredValue = parseQuantityInputValue(inputValue);
    if (Number.isNaN(desiredValue)) {
      desiredValue = value;
    }
    if (isMax && desiredValue <= min) {
      desiredValue = min + stack;
    } else if (desiredValue < min) {
      desiredValue = min;
    } else if (desiredValue > max) {
      desiredValue = max;
    }
    setInputValue(formatQuantityDisplayValue(desiredValue));
    // Disparamos onChange SIEMPRE al finalizar la edición manual,
    // incluso aunque el valor no cambie después del clamp
    onChange(desiredValue);
    return desiredValue;
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    event.stopPropagation();
    if (event.key === 'Enter') {
      event.preventDefault();
      const committedValue = finalizeInput();
      isEditingReference.current = false;
      event.currentTarget.blur();
      onCommit?.(committedValue);
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      finalizeInput();
      isEditingReference.current = false;
      event.currentTarget.blur();
    }
  };

  return (
    <div ref={quantityReference} className="quantity" role="group" aria-label={ariaLabel} tabIndex={-1}>
      <button
        type="button"
        aria-label={decrementLabel}
        onPointerDown={stopInteractionPropagation}
        onMouseDown={stopInteractionPropagation}
        onTouchStart={stopInteractionPropagation}
        onClick={(event) => {
          event.stopPropagation();
          let desiredValue = value - stack;
          if (desiredValue < min) {
            desiredValue = min;
          }
          if (isMax && desiredValue <= min) {
            desiredValue = min + stack;
          }
          setInputValue(formatQuantityDisplayValue(desiredValue));
          onChange(desiredValue);
        }}
      >
        <svg
          fill="none"
          viewBox="0 0 24 24"
          height="14"
          width="14"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2.5"
            stroke="var(--contrast)"
            d="M20 12L4 12"
          />
        </svg>
      </button>
      <input
        type="text"
        role="spinbutton"
        inputMode="decimal"
        pattern="-?[0-9]*[.,]?[0-9]*"
        step={stack}
        min={min}
        max={max}
        aria-label={ariaLabel}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={Number.isNaN(parsedInputValue) ? undefined : parsedInputValue}
        value={inputValue}
        onChange={handleInputChange}
        onPointerDown={stopInteractionPropagation}
        onMouseDown={stopInteractionPropagation}
        onTouchStart={stopInteractionPropagation}
        onFocus={(event) => {
          isEditingReference.current = true;
          selectInputContents(event.currentTarget);
        }}
        onBlur={() => {
          if (!isEditingReference.current) {
            return;
          }
          isEditingReference.current = false;
          finalizeInput();
        }}
        onKeyDown={handleKeyDown}
        onClick={(event) => {
          event.stopPropagation();
          selectInputContents(event.currentTarget);
        }}
        style={{
          appearance: 'none',
          MozAppearance: 'textfield',
          WebkitAppearance: 'none',
          border: 'none',
          outline: 'none',
          background: 'transparent',
          textAlign: 'center',
          width: '44px',
          minWidth: '44px',
        }}
      />
      <button
        type="button"
        aria-label={incrementLabel}
        onPointerDown={stopInteractionPropagation}
        onMouseDown={stopInteractionPropagation}
        onTouchStart={stopInteractionPropagation}
        onClick={(event) => {
          event.stopPropagation();
          let desiredValue = value + stack;
          if (desiredValue > max) {
            desiredValue = max;
          }
          setInputValue(formatQuantityDisplayValue(desiredValue));
          onChange(desiredValue);
        }}
      >
        <svg
          fill="none"
          viewBox="0 0 24 24"
          height="14"
          width="14"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2.5"
            stroke="var(--contrast)"
            d="M12 4V20M20 12H4"
          />
        </svg>
      </button>
    </div>
  );
}
